import { useMemo } from 'react';

import { ACCOUNT_SUBSCRIPTION_TYPE } from '@adtrackify/at-tracking-event-types';

import { useFacebookIntegrationConnected, useGoogleIntegrationConnected, useShopifyIntegrationConnected } from '@/features/destinations/hooks/use-destination-connected';
import { useDestinationStatus } from '@/features/setup/hooks/use-destination-status';
import {
  resolveLadder,
} from '@/features/setup/helpers/onboarding-ladder';
import { useAccountState } from '@/contexts/account/account-context';
import { usePixelDestinationsLoading } from '@/features/workspace/providers/account-stores-context';
import {
  OnboardingLadderResult,
} from '../types/use-onboarding-ladder-types';

export function useOnboardingLadder(): OnboardingLadderResult {
  const shopifyConnected = useShopifyIntegrationConnected();
  const facebookSourceConnected = useFacebookIntegrationConnected();
  const googleSourceConnected = useGoogleIntegrationConnected();
  const { isMetaConnected, isGoogleConnected, isKlaviyoConnected } = useDestinationStatus();

  const { currentAccount } = useAccountState();
  const onboarding = currentAccount?.onboarding;
  const trackingInstalled = onboarding?.trackingInstalled ?? false;
  const shopifyDataSharingDisabled = onboarding?.shopifyDataSharingDisabled ?? false;
  const klaviyoFlowsConfigured = onboarding?.klaviyoFlowsConfigured ?? false;

  const isShopifyAppCustomer =
    currentAccount?.accountSubscriptionType === ACCOUNT_SUBSCRIPTION_TYPE.SHOPIFY;
  const isPlatformMigrated = currentAccount?.isPlatformMigrated ?? false;

  const { isSuccess: signalsResolved, isError: signalsError } = usePixelDestinationsLoading();
  // Loading until resolved OR errored — error state allows page to render with best-effort data
  const isLoading = !signalsResolved && !signalsError || !currentAccount;

  return useMemo(
    () => ({
      ...resolveLadder({
        isShopifyAppCustomer,
        signals: {
          shopifyConnected: shopifyConnected || isShopifyAppCustomer || isPlatformMigrated,
          trackingInstalled,
          facebookSourceConnected,
          facebookDestinationEnabled: isMetaConnected,
          klaviyoConnected: isKlaviyoConnected,
          googleSourceConnected,
          googleDestinationEnabled: isGoogleConnected,
          shopifyDataSharingDisabled,
          klaviyoFlowsConfigured,
        },
      }),
      isLoading,
      isError: signalsError,
    }),
    [
      isLoading,
      signalsError,
      isShopifyAppCustomer,
      isPlatformMigrated,
      shopifyConnected,
      trackingInstalled,
      facebookSourceConnected,
      isMetaConnected,
      isKlaviyoConnected,
      googleSourceConnected,
      isGoogleConnected,
      shopifyDataSharingDisabled,
      klaviyoFlowsConfigured,
    ]
  );
}
